import { memo } from 'react';
import { css } from '@emotion/react';
import Head from 'next/head';
import { ArrowLeft, MessageSquare } from 'react-feather';
import { Divider } from '@loglass/loglass-ui/divider';
import { Heading } from '@loglass/loglass-ui/heading';
import { ExternalLink } from '@loglass/loglass-ui/link';
import { HStack } from '@loglass/loglass-ui/stack';
import { Span } from '@loglass/loglass-ui/span';
import { Button } from '@loglass/loglass-ui/button';
import { Tooltip } from '@loglass/loglass-ui/tooltip';
import { LoginUserInfoForAppLayout, Tenant } from '~/common/components/layout/AppLayout';
import { AppHeaderUserMenu } from '~/common/components/layout/app-header/AppHeaderUserMenu';
import { useHeaderInfo } from '~/common/components/layout/use-header-info';
import { InternalLink } from '~/common/components/link';
import { openZendeskWidget } from '~/app/ZendeskWidgetProvider';
import { useReportCommentsAccess } from '~/features/report-comments/report-comments-access';

type AppHeaderProps = {
  loginUser: LoginUserInfoForAppLayout;
  onLogout: () => void;
  tenant: Tenant;
};

const AppHeader = memo(function AppHeader({ onLogout, loginUser, tenant }: AppHeaderProps) {
  const { headerInfo } = useHeaderInfo();
  // 表示条件は一覧ページのガードと同じものを参照する（2箇所へ書くと片方だけ直す事故が起きる）。
  const isReportCommentsVisible = useReportCommentsAccess();

  return (
    <div css={styles.appHeader(headerInfo.backPageUrl != null)}>
      <Head>
        <title>{headerInfo.title}</title>
      </Head>

      {headerInfo.backPageUrl && (
        <div css={styles.pageTitleBackPageUrl}>
          <InternalLink href={headerInfo.backPageUrl}>
            <Button
              iconSize="m"
              variant="secondary"
              appearance="ghost"
              ariaLabel="return"
              icon={<ArrowLeft />}
              onClick={() => {
                if (!headerInfo.onBackButtonAction) return;
                headerInfo.onBackButtonAction();
              }}
              stopPropagation={false}
            />
          </InternalLink>
        </div>
      )}
      <div id="headerTitle">
        {!headerInfo.editable && <Heading level={2.5}>{headerInfo.title}</Heading>}
      </div>

      <HStack gap="16px" justify="end" align="center">
        <button css={styles.textButton} onClick={handleInquiryClick}>
          <Span level={2} css={styles.supportSectionLabel}>
            お問い合わせ
          </Span>
        </button>

        <ExternalLink href={SUPPORT_SITE_URL} target={'_blank'} showIcon={true}>
          <Span level={2} css={styles.supportSectionLabel}>
            サポートサイト
          </Span>
        </ExternalLink>

        {isReportCommentsVisible ? (
          <HStack gap="8px" align="center">
            <Divider color="gray" css={styles.divider} />
            <Tooltip message="コメント">
              <InternalLink href="/report-comments">
                <Button
                  iconSize="s"
                  variant="secondary"
                  appearance="ghost"
                  ariaLabel="コメント"
                  icon={<MessageSquare />}
                  stopPropagation={false}
                />
              </InternalLink>
            </Tooltip>
            <Divider color="gray" css={styles.divider} />
          </HStack>
        ) : (
          <Divider color="gray" css={styles.divider} />
        )}

        <AppHeaderUserMenu loginUser={loginUser} onLogout={onLogout} tenant={tenant} />
      </HStack>
    </div>
  );
});

const SUPPORT_SITE_URL = 'https://support.loglass.com';

/**
 * お問い合わせをクリックするとzendesk widgetをアクティベートします
 */
const handleInquiryClick = openZendeskWidget;

const styles = {
  appHeader: (backNav: boolean) => css`
    display: grid;
    grid-template-columns: ${backNav ? 'min-content' : ''} 1fr max-content;
    align-items: center;
    padding: 0 var(--spacing-7);
    border-bottom: var(--border-width-s) solid var(--color-neutral-2);
    background-color: var(--color-base-background);
    height: 56px;
  `,
  textButton: css`
    background-color: transparent;
    cursor: pointer;
  `,
  divider: css`
    height: 20px;
    margin: 0 var(--spacing-2);
  `,
  supportSectionLabel: css`
    word-break: keep-all;
    white-space: nowrap;
  `,
  pageTitleBackPageUrl: css`
    cursor: pointer;
    margin-right: var(--spacing-5);
  `,
};

export { AppHeader };
export type { AppHeaderProps };
